import { DataType, DateTime } from '@mode-switch/express';
import { Attr, Names, Values } from '@mode-switch/model';
import { QueryResult } from '@mode-switch/query';
import {
  AreaMarkOptions,
  BarMarkOptions,
  EncField,
  EncMarks,
  Encoding,
  MarkTransform,
  MarkType,
  SortType,
  VariableType,
} from '@mode-vegas/vegas';

import {
  EncodingHelpers,
  FacetSizeHelpers,
  FieldFormatHelpers,
  GRAND_TOTALS_LABEL,
  RenderDataModel,
  SUBTOTAL_TOKEN,
  SharedAxisHelpers,
  TOTALS_TOKEN,
  TOTAL_LABEL,
  TotalHelpers,
  buildMuzeDomainRangeMap,
  generateGradientStepColors,
  resolveFieldColor,
} from '@mode/charts/util-vegas';

import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledField } from '@mode/shared/util-switch';
import { hsl } from 'd3-color';
import { getFormatMapKeyByField } from 'libs/charts/util-vegas/src/lib/field-format-helpers';
import * as ChartData from './muze-chart-data';
import {
  MuzeDataSchema,
  MuzeDataset,
  buildMuzeTextFormatter,
  buildMuzeTextFormatterFromFormat,
  getAxisInterpolatorType,
  getAxisLabelFormat,
  getAxisScaleRange,
  getAxisTitleFormat,
  getHeaderLabelFormat,
  getHeaderTitleFormat,
  getLegendTitle,
  getLegendVisibility,
  getMuzeTextFormatter,
  loadMuzeData,
  makeSubtotalFormatter,
} from './muze-chart-data';
import {
  MuzeInteractionBrush,
  MuzeInteractionHighlight,
  MuzeInteractionInverseSelect,
  makeClassInteractionRule,
  makeFilterDomainSideEffect,
  makeInteractionFilter,
  makeInteractionHighlight,
  makeInteractionSideEffect,
  makeInteractionTarget,
  makePlotHighlighter,
} from './muze-chart-interactions';
import { getMarkChannelFieldId } from './muze-chart-layers';
import { TotalMuzeData, getTotalsSplitFields, makeSubtotalsData, makeTotalsData, makeTotalsFormatter } from './totals';

export function makeTotalsFormat(
  model: RenderDataModel,
  schema: MuzeDataSchema,
  encoding: Encoding,
  fieldFormatMap: ChartTypes.FieldFormatMap
): TotalsFormat {
  const totals: TotalsFormat = {};
  // We can't have binned fields in the totals data because a binned field
  // can't have a value of `$ALL$`. Muze is expecting a numeric value. Therefore,
  // we need to treat any totalable, binned field as categorical in the totals schema.
  const noBinnedSchema = schema.map((field) => {
    const encField = EncodingHelpers.getEncFieldById(encoding, field.name);
    return {
      ...field,
      subtype:
        encField && field.subtype === 'binned' && TotalHelpers.isTotalableEncField(encField)
          ? 'categorical'
          : field.subtype,
    };
  });

  const grandTotalsData = makeTotalsData(model, noBinnedSchema, encoding);

  const subtotalsData = makeSubtotalsData(model, noBinnedSchema, encoding);

  const totalData = mergeTotalsData(grandTotalsData, subtotalsData);

  const splitFields = getTotalsSplitFields(encoding);

  const formatter = makeTotalsFormatter(model, encoding, fieldFormatMap);

  if (totalData.row) {
    totals.rows = makeRowColumnTotalsConfig(
      totalData.row,
      noBinnedSchema,
      splitFields.row?.id,
      formatter,
      grandTotalsData.row != null,
      subtotalsData.row != null
    );
  }
  if (totalData.col) {
    totals.columns = makeRowColumnTotalsConfig(
      totalData.col,
      noBinnedSchema,
      splitFields.column?.id,
      formatter,
      grandTotalsData.col != null,
      subtotalsData.col != null
    );
  }

  return totals;
}

function mergeTotalsData(grand: TotalMuzeData, subtotal: TotalMuzeData) {
  const mergeDatasets = (a: MuzeDataset | undefined, b: MuzeDataset | undefined) => {
    if (!a) {
      return b;
    } else if (!b) {
      return a;
    }
    return a.concat(b);
  };

  return {
    col: mergeDatasets(grand.col, subtotal.col),
    row: mergeDatasets(grand.row, subtotal.row),
    all: mergeDatasets(grand.all, subtotal.all),
  };
}

function makeRowColumnTotalsConfig(
  data: MuzeDataset,
  schema: MuzeDataSchema,
  splitField: string | undefined,
  formatter: ChartData.MuzeDataFormatFn | undefined,
  grandTotalsEnabled: boolean,
  subtotalsEnabled: boolean
): RowColumnTotal {
  const dataModel = loadMuzeData(data, schema);
  const splitTotal = splitField !== undefined ? { field: splitField, text: TOTAL_LABEL } : {};
  return {
    totals: {
      grandTotals: {
        enabled: grandTotalsEnabled,
        text: GRAND_TOTALS_LABEL,
        splitTotal,
        formatter,
      },
      subTotals: {
        enabled: subtotalsEnabled,
        text: SUBTOTAL_TOKEN,
      },
      data: {
        dataModel: dataModel,
        totalString: TOTALS_TOKEN,
      },
    },
  };
}

export function makeLegendFormat(
  model: RenderDataModel,
  encoding: Encoding,
  format: ChartTypes.ChartFormat,
  fieldColors: ChartTypes.FieldColorMap,
  palette: readonly string[],
  fieldFormats: ChartTypes.FieldFormatMap
): Legend {
  const color = makeColorFormat(model, encoding, format, fieldColors, palette, fieldFormats);
  const size = makeSizeFormat(model, encoding, fieldFormats);
  const interaction = makeDefaultLegendInteraction();
  const inverseSelect = makeLegendInverseSelect();

  const isUseAsBackground =
    EncodingHelpers.supportsColorAsBackground(encoding, encoding.marks.properties.color.values[0]) &&
    encoding.marks.properties.color.target === 'background';
  const legend = isUseAsBackground ? { backgroundColor: color } : { color };

  return {
    ...legend,
    size,
    interaction: {
      ...interaction,
      ...inverseSelect,
    },
  };
}

function makeDefaultLegendInteraction() {
  const fadeInteractionRule = makeClassInteractionRule('exitSet', 'mode-plot-highlighter');
  const target = makeInteractionTarget(
    makeInteractionHighlight(makeInteractionSideEffect(makePlotHighlighter([fadeInteractionRule])))
  );

  return {
    highlight: target,
  };
}

/**
* To enable the locked domain feature during legend click interaction, we need to add the following
* interaction to the legend:

  legend: {
    interaction: {
      // Inverse select behavior gets triggered on legend
      'inverse-select': {
        // target property defines the behavioral actions which will get triggered on target components
        target: {
          layer: {
            // Dispatch filter behavior on layers when legend is clicked
            filter: {
              sideEffects: {
                // This filters the bars and updates the axis domains
                filter: {
                    // If lockDomain is true, then the axis domain does not change.
                    lockDomain: true
                },
                // Facet axis highlighter is a side effect which fades out the unselected axis labels and facets
                'facet-axis-highlighter': {
                    enabled: true
                },
              }
            }
          }
        }
      }
    }
  }
*/

function makeLegendInverseSelect() {
  const target = makeInteractionTarget(makeInteractionFilter(makeInteractionSideEffect(makeFilterDomainSideEffect())));
  return {
    'inverse-select': target,
  };
}

/**
 * Muze does not have an 'auto' legend placement. We need to translate our 'auto' into
 * 'top', 'right', 'bottom' or 'left' for the Muze legend configuration.
 */
export function resolveLegendPosition(encoding: Encoding, fieldFormat?: ChartTypes.FieldFormat) {
  const legendFormat = fieldFormat?.[ChartTypes.FieldFormatTypes.Legend];
  const definedPos = legendFormat?.position ?? 'auto';

  if (definedPos === 'auto') {
    // Default placement is 'right' when using auto except for the
    // cases explicitly handled below. These exceptions are to
    // match Quick Charts

    if (isTabularPieOrDonut(encoding)) {
      return 'bottom';
    }

    if (isXYWithSingleMeasureWithMeasureNamesOnColor(encoding)) {
      return 'top';
    }
    return 'right';
  }

  return definedPos;
}

function makeSizeFormat(
  model: RenderDataModel,
  encoding: Encoding,
  fieldFormats: ChartTypes.FieldFormatMap
): { fields: { [key: string]: LegendField } } {
  const fields: { [key: string]: LegendField } = {};

  /**
   * Gets legend fields for specified properties.
   *
   * @param {EncMarks} props properties
   * @param {[key: string]: LegendField} flds legend fields (modified)
   */
  const getSizeFields = (props: Readonly<EncMarks>, flds: { [key: string]: LegendField }) => {
    const summary = EncodingHelpers.getSummary(encoding);

    const sizeFieldId = getMarkChannelFieldId(encoding, 'size', props, summary);
    if (sizeFieldId === undefined) {
      return;
    }
    const sizeField = EncodingHelpers.getEncFieldById(encoding, sizeFieldId);
    if (sizeField?.id === undefined) {
      return;
    }

    // If a size field was found, store its legend properties.
    const sizeKey = getFormatMapKeyByField(sizeField);
    const fieldFormat = fieldFormats[sizeKey];
    const title = getLegendTitle(fieldFormat);
    const position = resolveLegendPosition(encoding, fieldFormat);
    const show = getLegendVisibility(fieldFormat);

    const sizeModeField = model.makeField(sizeField);
    const ordering = sizeModeField ? makeFieldOrdering(model, encoding, sizeModeField) : {};
    flds[sizeField.id] = {
      title,
      position,
      show,
      range: [4, 40], // approximately matches Quick Charts
      ordering,
    };
    const formatter = getMuzeTextFormatter(model, encoding, sizeModeField, fieldFormats);
    if (formatter) {
      flds[sizeField.id].item = {
        text: {
          formatter,
        },
      };
    }
  };

  // Get the legend fields from the series, if there are any...
  const { marks } = encoding;
  if (marks.series.length) {
    for (const series of marks.series) {
      getSizeFields(series.properties, fields);
    }
  }

  // ...otherwise, get a legend field from the marks.
  else {
    getSizeFields(marks.properties, fields);
  }

  return {
    fields,
  };
}

function makeColorFormat(
  model: RenderDataModel,
  encoding: Encoding,
  format: ChartTypes.ChartFormat,
  fieldColors: ChartTypes.FieldColorMap,
  palette: readonly string[],
  fieldFormats: ChartTypes.FieldFormatMap
) {
  const fields: { [key: string]: LegendField } = {};

  /**
   * Gets color map for specified field and properties.
   *
   * @param {EncMarks} props properties
   * @param {string} fld field
   * @param {[key: string]: LegendField} flds fields (modified)
   */
  const getColorMap = (props: Readonly<EncMarks>, fld: string | undefined, flds: { [key: string]: LegendField }) => {
    const summary = EncodingHelpers.getSummary(encoding);

    const colorFieldId = getMarkChannelFieldId(encoding, 'color', props, summary);
    if (colorFieldId === undefined) {
      return;
    }
    const colorField = EncodingHelpers.getEncFieldById(encoding, colorFieldId);

    if (colorField?.id !== undefined) {
      const formatKey = getFormatMapKeyByField(colorField);
      const fieldFormat = fieldFormats?.[formatKey];
      const title = getLegendTitle(fieldFormat);
      const position = resolveLegendPosition(encoding, fieldFormat);
      const show = getLegendVisibility(fieldFormat);

      const compiledField = model.compileEncField(colorField);
      const fieldColor = resolveFieldColor(compiledField, fieldColors);

      const range = fieldColor?.palette ?? palette;
      const colorModeField = model.makeField(colorField);
      const formatter = getMuzeTextFormatter(model, encoding, colorModeField, fieldFormats);

      // For a discrete field, store the domain/range map...
      if (colorField.variable.type === VariableType.DISCRETE) {
        const ordering = colorModeField ? makeFieldOrdering(model, encoding, colorModeField) : {};
        const rawDomainList = model.getColorDomain(encoding, format, fld, false);
        const domainRangeMap = buildMuzeDomainRangeMap(fieldColor, range, rawDomainList);

        const unassignedColor = fieldColor?.unassignedColor;
        const unassignedColorPalette = unassignedColor !== undefined ? [unassignedColor] : range;

        flds[colorField.id] = {
          title,
          position,
          show,
          range: unassignedColorPalette,
          repeatRange: true,
          domainRangeMap,
          ordering,
          item: {
            text: {
              formatter,
            },
          },
        };
      }

      // ...or for a continuous field, derive a range from the first color.
      // See https://github.com/mode/muze-issues/issues/248, https://github.com/mode/muze-issues/issues/249.
      // TODO: Missing values are colored black, so this light-to-dark range produces inconsistent graphs, to be fixed.
      else {
        const chartColor = props.color?.value || range[0];
        const measureNames = EncodingHelpers.findInEncoding(encoding, Names.Key);
        const splitLegend = fieldFormat?.[ChartTypes.FieldFormatTypes.Legend]?.splitLegend ?? false;

        if (splitLegend && colorField.formula === Values.Key && measureNames?.id != null) {
          // This is measure values and we have Measure Names somewhere.
          const values: Record<string, any> = {};
          encoding.value.forEach((field) => {
            if (field.id) {
              const compiledMVField = model.compileEncField(field);
              const mvFieldColor = resolveFieldColor(compiledMVField, fieldColors);

              const gradient = mvFieldColor?.gradient ?? fieldColor?.gradient;
              const { range, stops, domain } = makeGradientSpecification(
                model,
                encoding,
                colorField,
                chartColor,
                gradient,
                field
              );

              const mvModeField = model.makeField(field);
              // Each Measure Value field needs its own formatter
              const valueFieldFormatter = mvModeField
                ? buildMuzeTextFormatter(model, [mvModeField], fieldFormats)
                : undefined;
              const titleText =
                (mvModeField &&
                  FieldFormatHelpers.getBaseFormatDisplayName(
                    FieldFormatHelpers.getFormatMapKeyForModeField(mvModeField),
                    fieldFormats
                  )) ||
                '';
              const compiled = model.compileEncField(field);
              values[compiled.displayName] = {
                title: {
                  text: titleText,
                  show,
                },
                range: range.map((entry) => entry.color.toString()),
                domain,
                stops,
                step: gradient?.stepped ?? false,
                repeatRange: true,
                item: {
                  text: {
                    formatter: valueFieldFormatter,
                  },
                },
              };
            }
          });
          flds[colorField.id] = {
            title,
            position,
            show,
            domain: undefined,
            range: [],
            item: {
              text: {},
            },
            splitBy: {
              field: measureNames.id,
              values,
            },
          };
        } else {
          const compiledField = model.compileEncField(colorField);
          const fieldColor = resolveFieldColor(compiledField, fieldColors);
          const gradient = fieldColor?.gradient;
          const compiled = model.compileEncField(colorField);
          const { range, stops, domain } = makeGradientSpecification(model, encoding, colorField, chartColor, gradient);
          const encodingHasSingleValue = encoding.value.length === 1;
          const isMeasureValuesField = colorField.formula === Values.Key;
          const hasEmptyTitleText = !title.text;

          if (isMeasureValuesField && hasEmptyTitleText && encodingHasSingleValue) {
            // Check if display name is set for the values field
            const valuesField = model.makeField(encoding.value[0]);
            if (valuesField) {
              title.text =
                FieldFormatHelpers.getBaseFormatDisplayName(
                  FieldFormatHelpers.getFormatMapKeyForModeField(valuesField),
                  fieldFormats
                ) ?? '';
            }
          }

          flds[colorField.id] = {
            title,
            position,
            show,
            range: range.map((entry) => entry.color.toString()),
            domain,
            stops,
            step: (compiled.isBinned || gradient?.stepped) ?? false,
            repeatRange: true,
            item: {
              text: {
                formatter,
              },
            },
            splitBy: {
              field: null,
              values: {},
            },
          };
        }
      }
    }
    // otherwise, if a field is specified, store a single color from the properties. fallback to
    // the default color palette if no color is saved.
    else if (fld) {
      flds[fld] = {
        range: props.color.value ? [props.color.value] : palette.length ? [palette[0]] : [],
        repeatRange: true,
      };
    }
  };

  // Get the color map from the series, if there are any...
  const { marks } = encoding;
  if (marks.series.length) {
    for (const series of marks.series) {
      getColorMap(series.properties, series.field, fields);
    }
  }

  // ...otherwise, get a color map from the marks.
  else {
    getColorMap(marks.properties, undefined, fields);
  }

  return {
    invalidValueColor: '#ced1cd', // --ds-gray-600
    fields,
  };
}

/**
 * Muze defines the continuous gradient as three different terms:
 * - range: Array of colors
 * - stops: Array of numbers or a number describing the inflection
 *          points in the gradient. If it is a number rather than an array,
 *          it is the number of discrete steps in a stepped legend.
 * - domain: Array of numbers for the overall domain of values. This may
 *           be the min/max of the data or may be wider if the user has
 *           specified a range outside of the current data domain.
 * Generally, the size of the 'range' and 'stops' array should be the same.
 * Each color corresponds to an inflection point at each 'stop'.
 * The 'domain' is only necessary when the domain extends outside the
 * domain of the data. Otherwise it can be undefined and Muze will determine
 * the domain from the data.
 *
 */
export function makeGradientSpecification(
  model: RenderDataModel,
  encoding: Encoding,
  colorField: EncField,
  color: string,
  gradient: ChartTypes.ContinuousGradient | undefined,
  measureValueField?: EncField
) {
  // Get the standardized gradient colors
  let range = standardizeGradient(color, gradient);

  const { min, max } = getContinuousDomainMinMax(model, encoding, colorField, measureValueField);

  const userSpecifiedStepKeys = range.reduce((steps, entry) => {
    if (
      entry.key !== ChartTypes.GradientStep.DATA_MIN &&
      entry.key !== ChartTypes.GradientStep.DATA_MIDPOINT &&
      entry.key !== ChartTypes.GradientStep.DATA_MAX
    ) {
      steps.push(entry.key as number);
    }
    return steps;
  }, [] as number[]);

  if (gradient != null && userSpecifiedStepKeys.length > 0) {
    // Get the domain of the data.
    // Muze requires us to include the domain min and max. If we don't
    // Muze will add them and then misalign the size of 'range' and 'stops'.
    if (min == null || max == null) {
      return { range, stops: undefined, domain: undefined };
    }

    // Need to determine our stops.

    // If the gradient is using DATA_MIN, then the first stop is the domain min...
    //    __unless__ the first user specified step is less than the domain min.
    //    Ex: the domain min is 0. However, the gradient is defined as [DATA_MIN, -100, 100].
    //    We shouldn't create stops from [0, -100, 100]. It should be [-100, -100, 100].
    // Otherwise if it's not DATA_MIN, use the user specified value.
    const rangeMin =
      range[0].key === ChartTypes.GradientStep.DATA_MIN ? Math.min(min, userSpecifiedStepKeys[0]) : +range[0].key;

    // Same logic for the max
    // If the gradient is using DATA_MAX, then the last stop is the domain max...
    //  __unless__ the last user specfied step is greater than the domain max
    //    Ex: the domain max is 100. However, the gradient is defined as [DATA_MIN, 200, DATA_MAX].
    //    We shouldn't create stops from [0, 200, 100]. It should be [0, 200, 200].
    // Otherwise, if it's not the DATA_MAX, it's the user specified value.
    const rangeMax =
      range[range.length - 1].key === ChartTypes.GradientStep.DATA_MAX
        ? Math.max(max, userSpecifiedStepKeys[userSpecifiedStepKeys.length - 1])
        : +range[range.length - 1].key;

    const rangeMidpoint =
      range[1].key === ChartTypes.GradientStep.DATA_MIDPOINT ? (rangeMin + rangeMax) / 2 : +range[1].key;

    if (!gradient.stepped) {
      // Continuous gradient case.

      // Create the main stops
      const stops =
        gradient.gradientType === ChartTypes.GradientType.DIVERGING
          ? [rangeMin, rangeMidpoint, rangeMax]
          : [rangeMin, rangeMax];

      // If the user specified a start that is greater than the actual domain min, then
      // we need to add an extra stop and color for the domain min.
      if (min < stops[0]) {
        stops.unshift(min);
        range.unshift(range[0]);
      }
      // If the user specified an end that is less than the actual domain max, then
      // we need to add an extra stop and color for the domain max.
      if (max > stops[stops.length - 1]) {
        stops.push(max);
        range.push(range[range.length - 1]);
      }

      const domain = rangeMin < min || rangeMax > max ? [Math.min(min, rangeMin), Math.max(max, rangeMax)] : undefined;
      return { range: range, stops: stops, domain: domain };
    } else {
      // If the gradient is stepped, then we need to explicitly create the
      // stops for each step. There should be a minimum of two steps
      const numSteps = Math.max(gradient.numSteps ?? 0, 2);

      const rangeColors = range.map((entry) => entry.color.formatHex());

      // Generate a completely new set of ranges and stops. We have to explicitly
      // set each color in this scenario.
      range = [];
      const stops: Array<number> = [];

      if (gradient.gradientType === ChartTypes.GradientType.SEQUENTIAL) {
        // Generate colors from start to end
        const stepColors = generateGradientStepColors(rangeColors, numSteps);
        const stepSize = (rangeMax - rangeMin) / numSteps;
        for (let i = 0; i < numSteps; i++) {
          const stop = i === 0 ? Math.min(min, rangeMin) : rangeMin + i * stepSize;
          range.push({ key: stop, color: hsl(stepColors[i]) });
          stops.push(stop);
        }
        stops.push(Math.max(max, rangeMax));
      } else {
        // If there are only two steps just use the colors from the ends.
        // Otherwise generate colors from three color divergent palette.
        const stepColors =
          numSteps === 2 ? [rangeColors[0], rangeColors[2]] : generateGradientStepColors(rangeColors, numSteps);

        // The midpoint doesn't have to be equidistant between the start and end. As a result, the
        // step size may be different before and after the midpoint. For example, the range could
        // be [0, 25, 100]. If there were 4 steps, the first two would be 12.5 each. The second two
        // would be 37.5.
        // If there are 5 steps, then the middle step is half in the start and half in the end.
        // The step size before the midpoint would be 25 / 2.5 = 10.
        // The step size after the midpoint would be 75 / 2.5 = 30.
        // The middle step would be 10 / 2 + 30 / 2 = 20.
        const preMidpointStepSize = (rangeMidpoint - rangeMin) / (numSteps / 2);
        const postMidpointStepSize = (rangeMax - rangeMidpoint) / (numSteps / 2);
        const midpointStepSize = preMidpointStepSize / 2 + postMidpointStepSize / 2;
        const hasOddSteps = numSteps % 2 === 1;
        // Generate the stops
        let currentStop = rangeMin;
        // First stop is always the minimum of the domain and the user specified.
        stops.push(Math.min(min, rangeMin));
        for (let i = 0; i < numSteps; i++) {
          if (hasOddSteps && i === Math.floor(numSteps / 2)) {
            currentStop += midpointStepSize;
          } else if (i < numSteps / 2) {
            currentStop += preMidpointStepSize;
          } else {
            currentStop += postMidpointStepSize;
          }
          stops.push(currentStop);
        }
        // Ensure that the last stop is the maximum of the domain and the user specified max
        stops[stops.length - 1] = Math.max(max, stops[stops.length - 1]);
        // Generate the range from the stops
        range = stepColors.map((color, index) => {
          return { key: stops[index], color: hsl(color) };
        });
      }
      return { range: range, stops: stops as number[], domain: undefined };
    }
  } else {
    const isStepped = gradient?.stepped;
    const numSteps = isStepped ? Math.max(gradient.numSteps ?? 0, 2) : undefined;
    const colorRange = isStepped && numSteps === 2 ? [range[0], range[range.length - 1]] : range;
    const domain = min != null && max != null ? [min, max] : undefined;
    return { range: colorRange, stops: numSteps, domain: domain };
  }
}

/**
 * Generates an Array of objects containing the GradientStep key and color values
 * sequentially going from min to max. This guarantees that the color values can
 * be used in the order in the array.
 *
 * If no gradient is specified or the gradient is an old style (midpoint only), then
 * the gradient will be generated with the single color as the midpoint.
 */
export function standardizeGradient(color: string, gradient?: ChartTypes.ContinuousGradient) {
  if (gradient == null) {
    return generateGradientFromMidpoint(color, ChartTypes.GradientType.SEQUENTIAL);
  }
  const keys = Object.keys(gradient.colorEntries);
  if (keys.length === 1 && gradient.colorEntries[ChartTypes.GradientStep.DATA_MIDPOINT] != null) {
    // This is a special case for split legends fields in Measure Values were previously stored
    return generateGradientFromMidpoint(
      gradient.colorEntries[ChartTypes.GradientStep.DATA_MIDPOINT],
      gradient.gradientType
    );
  }
  // The colorEntries keys are either the known DATA_* values or a user specified number.
  // It will always include entries for all appropriate steps (2 for sequential,
  // 3 for diverging). Those may not be ordered correctly in the object.

  // Start with the implicit DATA_* keys in the expected order
  const implicitKeys =
    gradient.gradientType === ChartTypes.GradientType.DIVERGING
      ? [ChartTypes.GradientStep.DATA_MIN, ChartTypes.GradientStep.DATA_MIDPOINT, ChartTypes.GradientStep.DATA_MAX]
      : [ChartTypes.GradientStep.DATA_MIN, ChartTypes.GradientStep.DATA_MAX];

  // Get all of the user specified keys in ascending order.
  const userSpecifiedKeys = Object.keys(gradient.colorEntries)
    .reduce((keys, key) => {
      if (!implicitKeys.includes(key as ChartTypes.GradientStep)) {
        keys.push(+key);
      }
      return keys;
    }, [] as number[])
    .sort((a, b) => a - b);

  // Merge the keys together into a single ordered Array of keys. To do this,
  // we loop through the expected implicit keys. If the implicit key isn't in
  // the colorEntries, then use the next user specified key.
  const orderedKeys = implicitKeys
    .map((key) => {
      return gradient.colorEntries[key] != null ? (key as string) : (userSpecifiedKeys.shift() ?? (key as string));
    })
    .filter((value): value is ChartTypes.GradientStep | number => value != null);

  // Once the keys are in the correct order, map them to the objects containing
  // the key and color value
  return orderedKeys.map((key) => {
    return {
      key,
      color: hsl(gradient.colorEntries[key] ?? color),
    };
  });
}

/**
 * Helper function to generate a sequential or divergent gradient from a single color.
 * The specified color will be the midpoint with a lighter first color and a darker
 * last color. For DIVERGING, the specified color will be the midpoint.
 * The returned Array will contain an object with a 'key' which is the GradientStep
 * and a 'color' which is the HSL color.
 */
export function generateGradientFromMidpoint(color: string, gradientType: ChartTypes.GradientType) {
  const midpointColor = hsl(color).clamp();
  const maxLightnessShift = Math.min(midpointColor.l, 1 - midpointColor.l);
  const light = hsl(midpointColor.h, midpointColor.s, midpointColor.l + maxLightnessShift * 0.6);
  const dark = hsl(midpointColor.h, midpointColor.s, midpointColor.l - maxLightnessShift * 0.6);
  return gradientType === ChartTypes.GradientType.DIVERGING
    ? [
        { key: ChartTypes.GradientStep.DATA_MIN, color: light },
        { key: ChartTypes.GradientStep.DATA_MIDPOINT, color: midpointColor },
        { key: ChartTypes.GradientStep.DATA_MAX, color: dark },
      ]
    : [
        { key: ChartTypes.GradientStep.DATA_MIN, color: light },
        { key: ChartTypes.GradientStep.DATA_MAX, color: dark },
      ];
}

export function getContinuousDomainMinMax(
  model: RenderDataModel,
  encoding: Encoding,
  colorField: EncField,
  measureValueField: EncField | undefined
) {
  let seriesField: string | undefined;
  if (encoding.marks.channel !== '*') {
    seriesField = encoding.marks.series.find((s) => s.properties.color.values[0]?.id === colorField.id)?.field;
  }
  const rawDomainList = getRawDomainValues(model, encoding, seriesField, measureValueField);
  return rawDomainList.reduce(
    (minMax: { min: number | null; max: number | null }, val) => {
      if (val != null && Number.isFinite(val)) {
        const numVal = +val;
        if (minMax.min == null || numVal < minMax.min) {
          minMax.min = numVal;
        }
        if (minMax.max == null || numVal > minMax.max) {
          minMax.max = numVal;
        }
      }
      return minMax;
    },
    { min: null, max: null }
  );
}

function getRawDomainValues(
  model: RenderDataModel,
  encoding: Encoding,
  seriesField: string | undefined,
  measureValueField: EncField | undefined
) {
  if (measureValueField != null) {
    const allData = model.getSeriesData(seriesField ?? '*');
    const compiled = model.compileEncField(measureValueField);
    const seriesData = allData.filter((row) => row[Names.Key] === compiled.displayName);
    return seriesData.reduce<any[]>((values, data) => {
      values.push(data[Values.Key]);
      return values;
    }, []);
  }
  return model.getColorDomain(encoding, {} as ChartTypes.ChartFormat, seriesField, false);
}

export function makeFieldOrderingBasedOnMeasureValues(model: RenderDataModel, encoding: Encoding): Ordering | null {
  // Fields of the primary axis with dual-axis pairs accounted for. The array will include
  // discrete fields which we filter out before compiling and reducing `valueDisplayNames`
  let fields = Array.from(
    encoding.marks.channel === 'x'
      ? EncodingHelpers.buildBasicColumns(encoding)
      : EncodingHelpers.buildBasicRows(encoding)
  );

  /**
   * If `fields` includes Measure Values swap it out for the measures in `enc.value`, if any.
   * This will give us, if necessary, the series and Measure Values in order of occurrence
   *
   * Enc {
   *   y [
   *     SUM(discount)
   *     Measure Values
   *     SUM(price)
   *   ]
   *   value [
   *     AVG(rating)
   *   ]
   * }
   *
   * ...
   *
   * fields == [
   *  SUM(discount)
   *  AVG(rating)
   *  SUM(price)
   * ]
   */
  if (fields.some((field) => field.formula === Values.Key)) {
    for (const [index, field] of fields.entries()) {
      if (field.formula === Values.Key) {
        if (encoding.value.length !== 0) {
          fields.splice(index, 1, ...encoding.value);
        }
        break;
      }
    }
  }
  // Otherwise, use `enc.value` fields if Measure Values is in the encoding
  else if (EncodingHelpers.isInEncoding(encoding, Values.Key)) {
    if (encoding.value.length !== 0) {
      fields = encoding.value.slice();
    }
  }

  // Custom sort on value field names in the specified order
  const valueDisplayNames = fields
    // Kick out discrete fields from `buildBasic(Columns|Rows)`
    .filter((field) => field.variable.type === VariableType.CONTINUOUS)
    .reduce((all: string[], field) => {
      const compiled = model.compileEncField(field);
      if (compiled && compiled.isValid) {
        all.push(compiled.displayName);
      }
      return all;
    }, []);

  if (valueDisplayNames.length) {
    return {
      type: OrderingType.CUSTOM,
      values: valueDisplayNames,
    };
  } else {
    return null;
  }
}

export function makeFieldOrdering(
  model: RenderDataModel,
  encoding: Encoding,
  field: FlamingoTypes.ModeField
): Ordering {
  // Check for explicit sort
  const encSort = encoding.sorts.find((sort) => {
    return sort.type === SortType.NESTED
      ? // (Field Level) Nested sorts are by alias
        sort.field === field.alias
      : // (View Level) Everything else is by formula
        sort.source === field.formula.source;
  });

  if (encSort) {
    const direction = encSort.descending ? 'desc' : 'asc';
    if (encSort.type === SortType.FIELD) {
      const sortDomain = findSortDomain(model, encoding, field);
      if (sortDomain?.type === Attr.Type.Discrete) {
        return {
          type: OrderingType.CUSTOM,
          values: sortDomain.values,
        };
      } else {
        console.warn(
          `ByField ordered DomainResult for "${field.name}" is either undefined ` +
            `or has a type mismatch, defaulting to natural ascending`
        );
      }
    } else if (encSort.type === SortType.NESTED) {
      const useNestedConfig = hasNestedSortDomain(model, encoding, field);
      if (useNestedConfig) {
        return {
          type: OrderingType.NESTED,
          direction: 'asc',
          nested: {
            field: {
              name: encSort.byField.id as string,
              aggregation: 'min',
            },
          },
        };
      } else {
        console.warn(
          `ByNested ordered DomainResult for "${field.name}" is possibly undefined, ` +
            `defaulting to natural ascending`
        );
      }
    } else if (encSort.type === SortType.MANUAL) {
      const values = filterForCustomValues(encSort.items);
      if (values.length !== 0) {
        return {
          type: OrderingType.CUSTOM,
          direction,
          values,
        };
      }
    } else {
      return {
        type: OrderingType.NATURAL,
        direction,
      };
    }
  } else if (field.formula.source === Names.Key) {
    const ordering = makeFieldOrderingBasedOnMeasureValues(model, encoding);
    if (ordering) {
      return ordering;
    }
  }

  // Do the default sort
  return {
    type: OrderingType.NATURAL,
    direction: 'asc',
  };
}

function filterForCustomValues(items: readonly unknown[]) {
  return items.filter((value: unknown): value is OrderingValue => {
    switch (typeof value) {
      case 'string':
      case 'number':
      case 'boolean': {
        return true;
      }
      default: {
        return value === null;
      }
    }
  });
}

function findSortDomain(model: RenderDataModel, encoding: Encoding, field: FlamingoTypes.ModeField) {
  // Check for the sort domain keyed by this field
  let sortDomain = model.getSortDomain(field.alias);
  if (sortDomain != null) {
    return sortDomain;
  }
  // If no sort info found, it may be using the alias of a different field with the same formula.
  // Check all of the sort domains.
  const sortDomains = model.getSortDomain();
  for (const [fieldId, domain] of Object.entries(sortDomains)) {
    const encField = EncodingHelpers.getEncFieldById(encoding, fieldId);
    if (encField?.formula === field.formula.source) {
      return domain;
    }
  }
}

function hasNestedSortDomain(model: RenderDataModel, encoding: Encoding, field: FlamingoTypes.ModeField): boolean {
  return model.getNestedSortDomain(encoding, field.alias) !== undefined;
}

/**
 * Returns integer axis domain for continuous DATEPART field.
 *
 * Continuous DATEPARTs are converted internally to discrete.
 *
 * @param {RenderDataModel} model data model
 * @param {Encoding} encoding encoding
 * @param {FlamingoTypes.ModeField | undefined} field field
 * @returns {number[]} axis domain
 */
export function getContinuousDatePartDomain(
  model: RenderDataModel,
  encoding: Encoding,
  field: FlamingoTypes.ModeField | undefined
): number[] | undefined {
  let domain = undefined;
  let firstTick = 0;
  let lastTick = -1;
  let data: ReadonlyArray<QueryResult.Item>, alias: string, min: number, max: number;
  if (field) {
    switch (model.compileField(field).dateFuncUnit) {
      case DateTime.Unit.Year:
        // Create domain from min and max year.
        data = model.getSeriesData(encoding.marks.series[0]?.field ?? '*');
        alias = field.alias;
        min = data.find((item) => item[alias] != null)?.[alias];
        max = min;
        if (min !== undefined) {
          data.forEach((item) => {
            const value = item[alias];
            if (Number.isFinite(value)) {
              min = value < min ? value : min;
              max = value > max ? value : max;
            }
          });
        }

        // Min and max are either both undefined or both numeric at this point.
        if (min !== undefined && max !== undefined) {
          firstTick = min;
          lastTick = max;
        }
        break;
      case DateTime.Unit.Quarter:
        firstTick = 1;
        lastTick = 4;
        break;
      case DateTime.Unit.Month:
        firstTick = 1;
        lastTick = 12;
        break;
      case DateTime.Unit.Day:
        firstTick = 1;
        lastTick = 31;
        break;
      case DateTime.Unit.Weekday:
        lastTick = 6;
        break;
      case DateTime.Unit.Hour:
        lastTick = 23;
        break;
      default: // DateTime.Unit.Minute, DateTime.Unit.Second
        lastTick = 59;
        break;
    }
    if (firstTick <= lastTick) {
      domain = [];
      for (let i = firstTick; i <= lastTick; i++) {
        domain[i - firstTick] = i;
      }
    }
  }
  return domain;
}

// Returns axes formats.
export function makeAxesFormat(
  model: RenderDataModel,
  encoding: Encoding,
  formats: ChartTypes.FieldFormatMap,
  format?: ChartTypes.ChartFormat
): Axes {
  const xEncFields = [...encoding.x, ...encoding.x2];
  const yEncFields = [...encoding.y, ...encoding.y2];

  const xClassName =
    encoding.marks.channel === '*' && encoding.column.length === 0 ? 'mode-single-facet-header' : undefined;
  const yClassName =
    encoding.marks.channel === '*' && encoding.row.length === 0 ? 'mode-single-facet-header' : undefined;

  // Currently bar and area should always baseline the axis to zero. This could be a user option in the future.
  const requiresZeroBaseline =
    encoding.marks.channel !== '*' &&
    encoding.marks.series.some((series) => {
      return series.properties.type.value === MarkType.Bar || series.properties.type.value === MarkType.Area;
    });

  // For pivot tables we need to apply the proper alignment to the innermost discrete field since it will
  // be an axis but look like a facet. We could probably just always set the alignment since Muze seems to
  // ignore the labels alignment setting for a chart axis, but just to be safe we will only set it for discrete fields.
  const yAxisField =
    encoding.y.length === 1 && encoding.y[0].variable.type === VariableType.DISCRETE
      ? model.makeField(encoding.y[0])
      : undefined;
  const yLabelsAlign = yAxisField !== undefined ? { labels: { align: determineAlignment(model, yAxisField) } } : {};

  // Return the axis formats.
  return {
    x: baseAxis(
      {
        fields: makeAxisFieldsConfig(model, encoding, xEncFields, formats, xClassName, false, format),
        compact: useCompactAxis(encoding, 'x', format),
      },
      requiresZeroBaseline && EncodingHelpers.isDualAxis(encoding, 'x'),
      determineBestTickInterval(model, xEncFields)
    ),
    y: baseAxis(
      {
        fields: makeAxisFieldsConfig(model, encoding, yEncFields, formats, yClassName, true),
        compact: useCompactAxis(encoding, 'y', format),
        ...yLabelsAlign,
      },
      requiresZeroBaseline && EncodingHelpers.isDualAxis(encoding, 'y'),
      determineBestTickInterval(model, yEncFields)
    ),
  };
}

/**
 * Primary entry point for making all of the axis field configurations. This is the fields object in
 * this example:
 * axes: {
 *   y: {
 *    fields: {
 *      ...
 *     },
 *   },
 * }
 */
function makeAxisFieldsConfig(
  model: RenderDataModel,
  encoding: Encoding,
  encFields: EncField[],
  formats: ChartTypes.FieldFormatMap,
  className: string | undefined,
  isY: boolean,
  format?: ChartTypes.ChartFormat
) {
  const fields: Record<string, AxisField> = {};

  // Accumulate the formats.
  for (const encField of encFields) {
    const field = model.makeField(encField);
    if (encField.groupId == null && field) {
      const fieldKey = FieldFormatHelpers.getFormatMapKeyForModeField(field);
      const fieldFormats = formats[fieldKey];
      const axisTitleLocation = isY
        ? resolveYAxisTitleLocation(encoding, encField, fieldFormats)
        : resolveXAxisTitleLocation(encoding, encField, fieldFormats);
      const axisItem: AxisItem = {
        fieldFormats,
        type: field.type,
        axisTitleLocation,
        isY,
        className,
        format,
      };
      const isPrimaryRangeField = isPrimaryRangedAxisField(encoding, encField);
      const alias = isPrimaryRangeField ? makeRangeAxisFieldKey(encoding, encField) : field.alias;
      let axisField = makeAxisFieldFromField(field, model, encoding, axisItem, formats);
      if (isPrimaryRangeField) {
        axisField = {
          ...axisField,
          name: getRangeAxisTitle(model, encoding, encField, formats),
        };
      }
      fields[alias] = axisField;
    }
  }
  const groups = SharedAxisHelpers.buildGroupToMemberMap(encFields);

  Array.from(groups.entries()).forEach(([groupId, members]) => {
    const group = encoding.groups?.find((g) => g.id === groupId);
    if (group && members.length > 0) {
      const fieldKey = FieldFormatHelpers.getFormatMapKeyByGroup(group);
      const groupFormats = formats[fieldKey];

      const axisItem: AxisItem = {
        fieldFormats: groupFormats,
        type: Attr.Type.Continuous,
        axisTitleLocation: ChartTypes.HeaderAxisLocationTypes.Axis,
        isY,
      };
      const groupAlias = makeSharedAxisFieldKey(encoding, members);
      const groupField = SharedAxisHelpers.getEncFieldRepresentingGroup(group, encoding, model);
      if (groupField) {
        const modeField = model.makeField(groupField);
        if (modeField) {
          const titleFormat = getAxisTitleFormat(groupFormats);
          const displayName = FieldFormatHelpers.getBaseFormatDisplayName(fieldKey, formats);
          const title =
            titleFormat?.text != null && titleFormat.text !== '' ? titleFormat.text : displayName || group.name;
          fields[groupAlias] = {
            ...makeAxisFieldFromField(modeField, model, encoding, axisItem),
            name: title,
          };
        }
      }
    }
  });
  return fields;
}

function makeAxisFieldFromField(
  field: FlamingoTypes.ModeField,
  model: RenderDataModel,
  encoding: Encoding,
  axisItem: AxisItem,
  fieldFormatMap?: ChartTypes.FieldFormatMap
) {
  const fieldFormats: ChartTypes.FieldFormat | undefined = axisItem.fieldFormats;

  // Axis header label display
  const showValues =
    axisItem.type === Attr.Type.Discrete && fieldFormats
      ? fieldFormats[ChartTypes.FieldFormatTypes.Header].showValues?.show
      : true;

  // Axis name display
  const titleFormat =
    axisItem.type === Attr.Type.Continuous ? getAxisTitleFormat(fieldFormats) : getHeaderTitleFormat(fieldFormats);

  const isShowAxisName =
    axisItem.axisTitleLocation === ChartTypes.HeaderAxisLocationTypes.Axis ? (titleFormat?.show ?? true) : false;

  // Axis tick label display options
  const labelFormat =
    axisItem.type === Attr.Type.Continuous ? getAxisLabelFormat(fieldFormats) : getHeaderLabelFormat(fieldFormats);
  const showAxisLabels = labelFormat?.showLabels ?? true;
  const rotation = labelFormat?.rotation ?? undefined;
  const labels = showAxisLabels && Number.isFinite(rotation) ? { rotation } : { rotation: null };

  // Axis Scale
  const interpolator = axisItem.type === Attr.Type.Continuous ? getAxisInterpolatorType(fieldFormats) : undefined;

  // Axis tick order (discrete only)
  const ordering = axisItem.type === Attr.Type.Discrete ? makeFieldOrdering(model, encoding, field) : {};

  // Axis tick format function
  const tickFormat = makeAxisTickFormatter(field, model, encoding, fieldFormats, fieldFormatMap);

  // Axis domain range
  const domain = makeAxisTickDomain(field, model, encoding, fieldFormats, axisItem.isY);

  const binLabelsType =
    fieldFormats?.[ChartTypes.FieldFormatTypes.Axis][ChartTypes.FieldAxisTypes.BinnedAxisLabel]?.type ??
    ChartTypes.BinnedAxisLabelTypes.First;

  const baseAxis = baseAxisField({
    show: showValues,
    showAxisName: isShowAxisName,
    showInnerTicks: showAxisLabels,
    labels,
    interpolator,
    ordering,
    tickFormat,
    domain,
    bins: {
      ...(axisItem.type === Attr.Type.Discrete
        ? {
            position: 'center',
            display: 'wholeRange',
          }
        : {
            display:
              binLabelsType === ChartTypes.BinnedAxisLabelTypes.Auto ||
              binLabelsType === ChartTypes.BinnedAxisLabelTypes.First
                ? 'startValue'
                : 'continuous',
          }),
    },
  });
  // Add the following IFF they are set. Muze doesn't handle undefined well for these.
  if (Attr.Type.Continuous && isIndependentAxisDomain(fieldFormats)) {
    baseAxis.uniformAxisDomains = false;
  }
  if (axisItem.className != null) {
    baseAxis.className = axisItem.className;
  }

  return baseAxis;
}

function makeAxisTickFormatter(
  field: FlamingoTypes.ModeField,
  model: RenderDataModel,
  encoding: Encoding,
  fieldFormats: ChartTypes.FieldFormat,
  fieldFormatMap?: ChartTypes.FieldFormatMap
) {
  // Get tick format and axis domain.  For continuous DATEPARTs, use integers.
  // Continuous DATEPARTs are converted internally to discrete.
  return ChartData.treatContinuousDatePartAsDiscrete(model, encoding, field)
    ? rawFormatter
    : getAxisFieldTickFormat(model, encoding, field, fieldFormats, fieldFormatMap);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function rawFormatter(dataInfo: ChartData.MuzeTextDataInfo, contextInfo: ChartData.MuzeContextInfo) {
  return dataInfo.rawValue;
}

function makeAxisTickDomain(
  field: FlamingoTypes.ModeField,
  model: RenderDataModel,
  encoding: Encoding,
  fieldFormats: ChartTypes.FieldFormat,
  isY: boolean
) {
  let domain = getAxisFieldDomain(model.compileField(field), fieldFormats);
  if (ChartData.treatContinuousDatePartAsDiscrete(model, encoding, field)) {
    const fullDomain = getContinuousDatePartDomain(model, encoding, field);

    // Honor the user's specified domain, if any, as much as possible.
    // The domain may not be in range of the fullDomain, and may not be integral.
    // The domain can't be undefined, because the field is continuous, but TypeScript doesn't know that.
    // The fullDomain can be undefined, but only for YEARs, and only if the data set is empty.
    if (domain && fullDomain) {
      const firstTick = domain[0] !== null ? Math.round(domain[0]) : fullDomain[0];
      const lastTick = domain[1] !== null ? Math.round(domain[1]) : fullDomain[fullDomain.length - 1];
      domain = [];
      for (let i = firstTick; i <= lastTick; i++) {
        domain[i - firstTick] = i;
      }

      // Reverse the Y domain, because Muze orders discrete ticks from top to bottom.
      if (isY) {
        domain.reverse();
      }
    }
  }
  return domain;
}

export function makeRowsConfig(
  model: RenderDataModel,
  encoding: Encoding,
  formats: ChartTypes.FieldFormatMap,
  facetSizes: ChartTypes.FacetSizeMap,
  updateFacetSizesFunc?: UpdateFacetSizesFunction
): MuzeRowsColumnsConfig {
  return {
    facets: makeFacetConfig(model, encoding, [...encoding.row], formats),
    headers: makeHeaderConfig(
      model,
      encoding,
      [...encoding.row, ...encoding.y, ...encoding.y2],
      formats,
      Alignment.LEFT,
      true
    ),
    layout: makeLayoutConfig(facetSizes, [...encoding.y, ...encoding.row]),
    interaction: makeInteractionConfig(updateFacetSizesFunc),
    totalMaxWidthRatio: 1, // Muze will allow most of the table to be occupied by the row headers, but not all.
  };
}

export function makeColumnsConfig(
  model: RenderDataModel,
  encoding: Encoding,
  formats: ChartTypes.FieldFormatMap,
  facetSizes: ChartTypes.FacetSizeMap,
  updateFacetSizesFunc?: UpdateFacetSizesFunction
): MuzeRowsColumnsConfig {
  return {
    facets: makeFacetConfig(model, encoding, [...encoding.column], formats, Alignment.CENTER),
    headers: makeHeaderConfig(
      model,
      encoding,
      [...encoding.column, ...encoding.x, ...encoding.x2],
      formats,
      Alignment.CENTER,
      false
    ),
    layout: makeLayoutConfig(facetSizes, [...encoding.x, ...encoding.column]),
    interaction: makeInteractionConfig(updateFacetSizesFunc),
  };
}

/**
 * Returns facet size map from facet serializer.
 *
 * @param layout {FacetSerializer} layout
 * @returns {ChartTypes.FacetSizeMap} facet sizes
 */
export function getFacetSizes(layout: FacetSerializer): ChartTypes.FacetSizeMap {
  // Build facet size map from facet serializer.
  return FacetSizeHelpers.encodeFacetSizeMap(layout.serialize());
}

/**
 * Returns layout config from facet size map.
 *
 * @param facetSizes {ChartTypes.FacetSizeMap} facet size map
 * @returns {FacetSizeHelpers.FacetSizeItem[]} layout config
 */
export function makeLayoutConfig(
  facetSizes: ChartTypes.FacetSizeMap,
  fields: ReadonlyArray<EncField>
): FacetSizeHelpers.FacetSizeItem[] {
  // Build layout config from facet size map.
  const sizes = FacetSizeHelpers.decodeFacetSizeMap(facetSizes);
  // Filter the sizes to only include items whose target(s) are in the specified array of EncFields.
  // The items are either on Columns or Rows so we only need to find one match.
  // The target field may be null if there are no fields on that positional channel.
  return sizes.filter((layoutSize) => {
    return layoutSize.target.some(
      (target) => (target.field === null && fields.length === 0) || fields.some((field) => field.id === target.field)
    );
  });
}

/**
 * Makes interaction config using specified chart service.
 *
 * @param updateFacetSizesFunc {UpdateFacetSizesFunction|undefined} update function
 * @returns {ColumnVectorInteraction} interaction config
 */
export function makeInteractionConfig(updateFacetSizesFunc?: UpdateFacetSizesFunction): ColumnVectorInteraction {
  return {
    resize: {
      enabled: !!updateFacetSizesFunc,
      sideEffects: {
        'column-dimension-changer': { widths: updateFacetSizesFunc },
      },
    },
  };
}

export function makeFacetConfig(
  model: RenderDataModel,
  encoding: Encoding,
  facetEncFields: EncField[],
  formats: ChartTypes.FieldFormatMap,
  alignment: Alignment | undefined = undefined
): Facets {
  const fields: Record<string, FacetField> = {};
  facetEncFields.forEach((encField, index) => {
    const field = model.makeField(encField);
    if (field) {
      const fieldKey = FieldFormatHelpers.getFormatMapKeyForModeField(field);
      const fieldFormats = formats[fieldKey];

      // Include the parent facet field if there is one
      const parentField = facetEncFields[index - 1] != null ? model.makeField(facetEncFields[index - 1]) : undefined;
      const formatter = getMuzeTextFormatter(model, encoding, field, formats, parentField);
      const showValues =
        field.type === Attr.Type.Discrete && fieldFormats
          ? (fieldFormats[ChartTypes.FieldFormatTypes.Header].showValues?.show ?? true)
          : true;
      const ordering = makeFieldOrdering(model, encoding, field);
      // Outermost facet of a tabular layout gets special styling
      const className = index === 0 && encoding.marks.channel === '*' ? 'mode-outermost-facet-header' : undefined;
      const align = alignment != null ? alignment : determineAlignment(model, field);
      const labels = {
        align,
      };
      fields[field.alias] = { show: showValues, formatter, ordering, className, labels };
    }
  });
  return {
    show: true,
    fields,
  };
}

export function makeHeaderConfig(
  model: RenderDataModel,
  encoding: Encoding,
  headerEncFields: EncField[],
  formats: ChartTypes.FieldFormatMap,
  alignment: Alignment,
  isRow: boolean
): Headers {
  const fields: Record<string, HeaderField> = {};
  let isShowHeader = false;
  // Configure the header fields
  headerEncFields.forEach((encField) => {
    const field = model.makeField(encField);
    if (field) {
      let isShow = false;
      if (field.type === Attr.Type.Discrete) {
        const fieldKey = FieldFormatHelpers.getFormatMapKeyForModeField(field);
        const fieldFormats = formats[fieldKey];
        const titleFormat = getHeaderTitleFormat(fieldFormats);
        const axisTitleLocation = isRow
          ? resolveYAxisTitleLocation(encoding, encField, fieldFormats)
          : resolveXAxisTitleLocation(encoding, encField, fieldFormats);
        isShow = axisTitleLocation === ChartTypes.HeaderAxisLocationTypes.Header && (titleFormat?.show ?? true);
      }
      const isPrimaryRangeField = isPrimaryRangedAxisField(encoding, encField);
      const alias = isPrimaryRangeField ? makeRangeAxisFieldKey(encoding, encField) : field.alias;
      fields[alias] = { show: isShow };
      if (isShow) {
        isShowHeader = true;
      }
    }
  });
  // Configure any header groups. The shared axis label is always on the
  // axis so we need to explicitly turn off the label on the header. Otherwise,
  // it would appear twice.
  const groups = SharedAxisHelpers.buildGroupToMemberMap(headerEncFields);
  Array.from(groups.values()).forEach((members) => {
    if (members.length > 0) {
      const fieldKey = makeSharedAxisFieldKey(encoding, members);
      fields[fieldKey] = {
        show: false,
      };
    }
  });
  return {
    className: 'mode-column-header-container',
    show: isShowHeader,
    fields,
    align: alignment,
    enableDirectSort: false,
  };
}

/**
 * Returns borders configuration.
 *
 * TODO: This heuristic is roughly consistent with previous behavior; it could be improved with additional Muze API.
 *
 * @param {RenderDataModel} model model
 * @param {Encoding} encoding encoding
 * @param {MuzeRowsColumnsConfig} rows rows config
 * @param {MuzeRowsColumnsConfig} columns columns config
 * @return {MuzeBordersConfig} borders configuration
 */
export function makeBordersConfig(
  model: RenderDataModel,
  encoding: Encoding,
  rows: MuzeRowsColumnsConfig,
  columns: MuzeRowsColumnsConfig
): MuzeBordersConfig {
  const nRows = encoding.row.length;
  const nCols = encoding.column.length;
  const nYs = encoding.y.length + encoding.y2.length;
  const nXs = encoding.x.length + encoding.x2.length;
  const hasAny = nRows + nCols + nYs + nXs > 0;
  const hasAllDiscrete = [...encoding.y, ...encoding.y2, ...encoding.x, ...encoding.x2].every((encField) => {
    const field = model.makeField(encField);
    return !field || field.type === Attr.Type.Discrete;
  });
  const hasRowBorders = rows.headers.show && (nRows > 1 || (hasAny && hasAllDiscrete));
  const hasColumnBorders = columns.headers.show && (nCols > 1 || (hasAny && hasAllDiscrete));
  const hasBottomLeftBorders = hasAny;
  const hasTopRightBorders = hasRowBorders || hasColumnBorders || nRows + nYs > 1 || nCols + nXs > 1;
  return {
    border: {
      width: 1,
      color: '#cbcbcb',
      showRowBorders: { top: hasRowBorders, bottom: hasRowBorders, left: false, right: false },
      showColBorders: { top: false, bottom: false, left: hasColumnBorders, right: hasColumnBorders },
      showValueBorders: {
        top: hasTopRightBorders,
        bottom: hasBottomLeftBorders,
        left: hasBottomLeftBorders,
        right: hasTopRightBorders,
      },
    },
  };
}

function isIndependentAxisDomain(fieldFormats: ChartTypes.FieldFormat): boolean {
  return fieldFormats?.axisFormat.scale?.range?.isIndependent ? true : false;
}

export function getAxisFieldTickFormat(
  model: RenderDataModel,
  encoding: Encoding,
  field: FlamingoTypes.ModeField,
  fieldFormats: ChartTypes.FieldFormat,
  fieldFormatMap?: ChartTypes.FieldFormatMap
): AxisField['tickFormat'] | undefined {
  let textFormat = resolveAppliedTextFormat(field, fieldFormats);

  if (field.source === Attr.Source.Values) {
    // Special handling for Measure Values
    // When there is a single measure, we should use the formatting from that measure
    // if the Measure Values formatting is automatic.
    if (FieldFormatHelpers.isAutomaticShortcutType(textFormat?.shortcutType) && encoding.value.length === 1) {
      const valueField = model.makeField(encoding.value[0]);
      if (valueField != null) {
        const fieldKey = FieldFormatHelpers.getFormatMapKeyForModeField(valueField);
        const valueTextFormat = resolveAppliedTextFormat(valueField, fieldFormatMap?.[fieldKey]);

        textFormat = FieldFormatHelpers.resolveTextFormatForCompiledField(
          model.compileField(valueField),
          valueTextFormat
        );
      }
    }
  } else {
    // Non-system fields may have a default format from the view attrs.
    textFormat = FieldFormatHelpers.resolveTextFormatForCompiledField(model.compileField(field), textFormat);
  }

  if (FieldFormatHelpers.isAutomaticShortcutType(textFormat?.shortcutType)) {
    // Apply any automatic overrides
    textFormat = overrideAxisAutomaticFormat(encoding, field, textFormat);
  }

  const parentTextFormat = getParentFacetFormat(field, model, encoding, fieldFormatMap);

  const formatter = textFormat
    ? buildMuzeTextFormatterFromFormat(
        model,
        [{ field, format: textFormat }],
        undefined,
        makeSubtotalFormatter(model, parentTextFormat)
      )
    : fieldFormatMap && getMuzeTextFormatter(model, encoding, field, fieldFormatMap, parentTextFormat?.field);

  if (formatter) {
    return function (dataInfo, contextInfo) {
      return formatter(dataInfo, contextInfo);
    };
  }
}

function resolveAppliedTextFormat(field: FlamingoTypes.ModeField, fieldFormat: ChartTypes.FieldFormat | undefined) {
  return field.type === Attr.Type.Continuous
    ? // Axis TextFormat for Continuous
      FieldFormatHelpers.resolveAxisTextFormat(fieldFormat)
    : // Header TextFormat for Discrete. Ideally, Muze will
      // add support for formatting Discrete Axis headers and
      // this will "just work"
      FieldFormatHelpers.resolveHeaderTextFormat(fieldFormat);
}

function getParentFacetFormat(
  field: FlamingoTypes.ModeField,
  model: RenderDataModel,
  encoding: Encoding,
  fieldFormatMap?: ChartTypes.FieldFormatMap
) {
  const hasField = (fields: readonly Readonly<EncField>[]) => fields.some((f) => f.id === field.alias);

  // Get the last field on the facets (ie rows or columns) if there is one
  const parentField = (() => {
    const facetFields = hasField(encoding.y) ? encoding.row : hasField(encoding.x) ? encoding.column : [];
    const parentFacetField = facetFields[facetFields.length - 1];
    return parentFacetField ? model.makeField(parentFacetField) : undefined;
  })();

  if (parentField == null) {
    return;
  }

  const fieldKey = FieldFormatHelpers.getFormatMapKeyForModeField(parentField);
  // Get the applied text format
  let parentTextFormat = resolveAppliedTextFormat(parentField, fieldFormatMap?.[fieldKey]);
  // Resolve the format against any default attr formats
  parentTextFormat = FieldFormatHelpers.resolveTextFormatForCompiledField(
    model.compileField(parentField),
    parentTextFormat
  );

  return {
    field: parentField,
    format: parentTextFormat,
  };
}

export function overrideAxisAutomaticFormat(
  encoding: Encoding,
  field: FlamingoTypes.ModeField,
  textFormat: ChartTypes.TextFormat | undefined
) {
  // Override Auto with Percent for 100% Stacked chart
  const series = encoding.marks.series.find((s) => s.field === field.alias);
  const seriesMarkType = series?.properties.type.value;
  if (seriesMarkType === MarkType.Bar || seriesMarkType === MarkType.Area) {
    const options = series?.properties.options?.[seriesMarkType] as BarMarkOptions | AreaMarkOptions;
    if (options?.transform === MarkTransform.Stack100Percent) {
      // Create a percent format
      textFormat = {
        type: ChartTypes.FormatType.Percent,
        colType: DataType.Name.Float,
        shortcutType: ChartTypes.FormatShortcutType.Percent,
        precision: 0,
        percentTransform: true,
        separator: textFormat?.separator,
        grouping: textFormat?.grouping,
      };
    }
  }
  return textFormat;
}

function getAxisFieldDomain(compiled: CompiledField, fieldFormats: ChartTypes.FieldFormat): AxisField['domain'] {
  if (compiled.isContinuous && !compiled.isBinned) {
    if (isIndependentAxisDomain(fieldFormats)) {
      return [null, null];
    }

    switch (compiled.formulaDataType) {
      case DataType.Name.Float:
      case DataType.Name.Integer:
      case DataType.Name.Decimal:
      case DataType.Name.Timestamp: {
        const range = getAxisScaleRange(fieldFormats);
        const min = typeof range?.min === 'number' ? range.min : null;
        const max = typeof range?.max === 'number' ? range.max : null;
        return [min, max];
      }
    }
  }
}

/**
 * Determines where the x-axis title should be shown for the specified field from
 * the field formats. The options are Axis or Header.
 *
 * For Auto:
 *  - Place on the axis if not faceted and not a pivot table
 *  - Place on the header otherwise.
 */
function resolveXAxisTitleLocation(
  encoding: Encoding,
  field: EncField,
  fieldFormat: ChartTypes.FieldFormat
): ChartTypes.HeaderAxisLocationTypes.Axis | ChartTypes.HeaderAxisLocationTypes.Header {
  // Continuous fields are always on the axis
  if (field.variable.type === VariableType.CONTINUOUS) {
    return ChartTypes.HeaderAxisLocationTypes.Axis;
  }

  // If this field is not on the x-axis, it is a header
  if (![...encoding.x].some((f) => f === field)) {
    return ChartTypes.HeaderAxisLocationTypes.Header;
  }

  const headerTitleFormat = getHeaderTitleFormat(fieldFormat);
  const location = headerTitleFormat?.location ?? ChartTypes.HeaderAxisLocationTypes.Auto;
  // For pivot tables, we always use show on header
  if (isTextTable(encoding)) {
    return ChartTypes.HeaderAxisLocationTypes.Header;
  }
  // If location is not auto, then return the specified location
  if (location !== ChartTypes.HeaderAxisLocationTypes.Auto) {
    return location;
  }
  // For Auto, only show on axis if there are no facets (e.g. simple x-y chart)
  const isFaceted = encoding.marks.channel === 'y' && encoding.column.length > 0;
  return isFaceted ? ChartTypes.HeaderAxisLocationTypes.Header : ChartTypes.HeaderAxisLocationTypes.Axis;
}

/**
 * Determines where the y-axis title should be shown for the specified field from
 * field formats. The options are Axis or Header.
 *
 * For Auto, the title goes on the header.
 */
function resolveYAxisTitleLocation(
  encoding: Encoding,
  field: EncField,
  fieldFormat: ChartTypes.FieldFormat
): ChartTypes.HeaderAxisLocationTypes.Axis | ChartTypes.HeaderAxisLocationTypes.Header {
  // Continuous fields are always shown
  if (field.variable.type === VariableType.CONTINUOUS) {
    return ChartTypes.HeaderAxisLocationTypes.Axis;
  }

  // If this field is not on the x-axis, return false
  if (![...encoding.y].some((f) => f === field)) {
    return ChartTypes.HeaderAxisLocationTypes.Header;
  }
  const headerTitleFormat = getHeaderTitleFormat(fieldFormat);
  const location = headerTitleFormat?.location ?? ChartTypes.HeaderAxisLocationTypes.Auto;
  // If location is not auto or axis, then do not show
  if (location !== ChartTypes.HeaderAxisLocationTypes.Auto) {
    return location;
  }
  // For auto, we hide the y axis name and use the header (return false). If Axis, then show.
  return ChartTypes.HeaderAxisLocationTypes.Header;
}

function determineAlignment(model: RenderDataModel, field: FlamingoTypes.ModeField) {
  const compiled = model.compileField(field);
  return compiled.isValid && compiled.isAttrNumberType ? Alignment.RIGHT : Alignment.LEFT;
}

export function isTextTable(encoding: Encoding) {
  return encoding.marks.channel === '*' && encoding.marks.properties.type.value === MarkType.Text;
}

/**
 * Determines if this encoding is a pie or donut which is not rendered in an x-y cartesian
 * plot. This is any pie/donut where the encoding is '*' including cases where there
 * are facets on rows and/or columns.
 */
function isTabularPieOrDonut(encoding: Encoding) {
  return (
    encoding.marks.channel === '*' &&
    (encoding.marks.properties.type.value === MarkType.Pie || encoding.marks.properties.type.value === MarkType.Donut)
  );
}

/**
 * Identifies if this encoding is a very specific case of a single measure in an x-y plot
 * where it uses Measure Names for color.
 */
function isXYWithSingleMeasureWithMeasureNamesOnColor(encoding: Encoding) {
  // Must be an x | y encoding with only 1 series
  if (encoding.marks.channel === '*' || encoding.marks.series.length !== 1) {
    return false;
  }
  // Must have Measure Names on color
  if (encoding.marks.series[0].properties.color.values[0]?.formula !== Names.Key) {
    return false;
  }

  // Must have only one field on the primary axis
  const primaryFields = encoding.marks.channel === 'x' ? encoding.x : encoding.y;
  if (primaryFields.length !== 1) {
    return false;
  }
  // Finally, check number of Values if the primary field is Measure Values
  if (primaryFields[0].formula === Values.Key) {
    return encoding.value.length === 1;
  }
  return true;
}

export function getBaselineFieldIdForSeries(encoding: Encoding, seriesField: EncField): string | undefined;
export function getBaselineFieldIdForSeries(encoding: Encoding, seriesFieldId: string): string | undefined;
export function getBaselineFieldIdForSeries(encoding: Encoding, seriesField: EncField | string): string | undefined {
  const seriesId = typeof seriesField === 'string' ? seriesField : (seriesField.id ?? '');

  const series = encoding.marks.series.find((s) => s.field === seriesId);
  if (series && EncodingHelpers.supportsBaselineChannel(series.properties.type.value)) {
    const summary = EncodingHelpers.getSummary(encoding);
    return getMarkChannelFieldId(encoding, 'baseline', series.properties, summary);
  }
}

export function getBaselineFieldForSeries(encoding: Encoding, field: EncField) {
  const baselineId = getBaselineFieldIdForSeries(encoding, field);
  return baselineId ? EncodingHelpers.getEncFieldById(encoding, baselineId) : undefined;
}

export function makeDefaultRangeAxisTitle(
  start: CompiledField,
  end: CompiledField,
  fieldFormatMap?: ChartTypes.FieldFormatMap
) {
  const getDisplayNameForField = (field: CompiledField) => {
    const key = FieldFormatHelpers.getFormatMapKey(
      field.formula,
      field.varType === Attr.Type.Discrete ? VariableType.DISCRETE : VariableType.CONTINUOUS
    );
    const displayName = FieldFormatHelpers.getBaseFormatDisplayName(key, fieldFormatMap);
    return displayName || field.displayName;
  };
  const startDisplayName = getDisplayNameForField(start);
  const endDisplayName = getDisplayNameForField(end);
  return `Range of ${startDisplayName} to ${endDisplayName}`;
}

function isPrimaryRangedAxisField(encoding: Encoding, field: EncField) {
  return getBaselineFieldForSeries(encoding, field) != null;
}

function makeRangeAxisFieldKey(encoding: Encoding, field: EncField) {
  // Key for a ranged axis is the baseline id joined with the series field with a ','.
  // If the series field is also part of a shared axis group, all of the group field ids
  // are included before the ranged axis fields.
  const baselineField = getBaselineFieldForSeries(encoding, field);
  if (baselineField) {
    const axisFields = new Set<string>();
    const sharedAxisGroup = encoding.groups?.find((group) => group.id === field.groupId);
    if (sharedAxisGroup) {
      SharedAxisHelpers.getGroupMembersInEncoding(sharedAxisGroup, encoding).forEach((field) => {
        axisFields.add(field.id ?? '');
      });
    }
    axisFields.add(baselineField.id ?? '');
    axisFields.add(field.id ?? '');
    return Array.from(axisFields).join(',');
  }
  return field.id ?? '';
}

function getRangeAxisTitle(
  model: RenderDataModel,
  encoding: Encoding,
  field: EncField,
  fieldFormatMap?: ChartTypes.FieldFormatMap
) {
  const getTitleFormatForField = (encField: EncField) => {
    const key = FieldFormatHelpers.getFormatMapKeyByField(encField);
    return getAxisTitleFormat(fieldFormatMap?.[key]);
  };

  // Use the title set on the primary axis field if set.
  const primaryTitleFormat = getTitleFormatForField(field);
  if (primaryTitleFormat?.text) {
    return primaryTitleFormat.text;
  }

  // Otherwise concatenate the two display names together.
  const endField = model.compileEncField(field);

  const baselineEncField = getBaselineFieldForSeries(encoding, field);
  // If no baseline field, just return the field display name
  if (baselineEncField == null) {
    const key = FieldFormatHelpers.getFormatMapKeyByField(field);
    const displayName = FieldFormatHelpers.getBaseFormatDisplayName(key, fieldFormatMap);
    return displayName || endField.displayName;
  }

  const startField = model.compileEncField(baselineEncField);
  return makeDefaultRangeAxisTitle(startField, endField, fieldFormatMap);
}

function useCompactAxis(encoding: Encoding, axis: 'x' | 'y', format?: ChartTypes.ChartFormat) {
  // If the compact flag is not set or is explicitly false, then return false.
  if (format?.xAxis.compact !== true) {
    return false;
  }
  // We can't compact a text mark in a tabular layout
  if (encoding.marks.channel === '*' && encoding.marks.properties.type.value === MarkType.Text) {
    return false;
  }
  // With compact set to true, make sure that the requested axis field is discrete.
  const axisField = axis === 'x' ? encoding.x[0] : encoding.y[0];
  return axisField?.variable.type === VariableType.DISCRETE;
}

/**
 * In the config, fields are identified by the EncField id. A shared axis Muze object
 * is identified by a concatenation of the id's of all of the members.
 * Ex: '2e2c,ee3b,a11b'
 */
function makeSharedAxisFieldKey(encoding: Encoding, members: EncField[]) {
  const memberIds = members.map((memberField) => memberField.id ?? '');

  // Check if any of these fields are also part of a range.
  // Group field ids come first, followed by the baseline fields.
  const allSharedIds = new Set<string>(memberIds);
  members.forEach((field) => {
    const baselineFieldId = getBaselineFieldIdForSeries(encoding, field);
    if (baselineFieldId) {
      allSharedIds.add(baselineFieldId);
    }
  });
  return Array.from(allSharedIds).join(',');
}

// Returns tick interval given fields and ids of hidden field names.
function determineBestTickInterval(model: RenderDataModel, encFields: EncField[]) {
  let tickInterval = undefined;
  encFields.some((encField) => {
    const compiledField = model.compileEncField(encField);
    if (compiledField?.hasDateTrunc) {
      const yearStart = compiledField.yearStart ?? compiledField.defaultYearStart;
      const weekStart = compiledField.weekStart ?? compiledField.defaultWeekStart;
      switch (compiledField.dateFuncUnit) {
        case DateTime.Unit.Year:
          tickInterval = {
            step: 'year',
            start: <number>DateTime.yearStartMonth(yearStart) - 1,
          };
          break;
        case DateTime.Unit.Quarter:
          tickInterval = {
            step: 'month',
            multiplier: 3, // For quarters, use 'month' with a multiplier of 3. No native Muze support for quarter
            // 'start' for 'month' is the day of the month, not the month of the year. Waiting on Muze support.
          };
          break;
        case DateTime.Unit.Month:
          tickInterval = {
            step: 'month',
          };
          break;
        case DateTime.Unit.Week:
          tickInterval = {
            step: 'week',
            start: DateTime.weekStartDow(weekStart),
          };
          break;
        case DateTime.Unit.Day:
          tickInterval = {
            step: 'day',
          };
          break;
        case DateTime.Unit.Hour:
          tickInterval = {
            step: 'hour',
          };
          break;
        case DateTime.Unit.Minute:
          tickInterval = {
            step: 'minute',
          };
          break;
        default:
          tickInterval = {
            step: 'second',
          };
          break;
      }
      return true;
    }
    return false;
  });
  return tickInterval;
}

export type Axes = {
  x: Axis;
  y: Axis;
};

export type TickInterval = {
  step: 'millisecond' | 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year';
  multiplier: number;
  start?: number; // (0-based) For step='week': start day of week. For step='year': start month of year.
};

export type Axis = {
  alignZeroLine: boolean;
  transition: {
    disabled: boolean;
  };
  fields: {
    [key: string]: AxisField;
  };
  tickInterval?: TickInterval;
  labels?: {
    align: 'left' | 'center' | 'right';
  };
  compact: boolean;
  enableDirectSort: boolean;
  showAxisLine: boolean;
};

export type Facets = {
  show: boolean;
  fields: {
    [key: string]: FacetField;
  };
};

export type FacetField = {
  show: boolean;
  formatter?: ChartData.MuzeDataFormatFn;
  ordering?: Ordering;
  className?: string;
  labels?: {
    align: Alignment;
  };
};

export enum Alignment {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

export type Headers = {
  show: boolean;
  fields: {
    [key: string]: HeaderField;
  };
  className?: string;
  align?: Alignment;
  enableDirectSort: boolean;
};

export type HeaderField = {
  show: boolean;
};

function baseAxis(props: Partial<Axis> = {}, alignZeroLine: boolean, tickInterval?: TickInterval): Axis {
  const axis = {
    fields: {},
    alignZeroLine,
    transition: {
      disabled: true,
    },
    compact: false,
    enableDirectSort: false,
    showAxisLine: true,
    ...props,
  };
  if (tickInterval) {
    axis.tickInterval = tickInterval;
  }
  return axis;
}

export type TotalsFormat = {
  rows?: RowColumnTotal;
  columns?: RowColumnTotal;
};

export type RowColumnTotal = {
  totals: RowColumnTotals;
};

export type RowColumnTotals = {
  grandTotals: {
    enabled: boolean;
    formatter?: ChartData.MuzeDataFormatFn | undefined;
    text: string;
    splitTotal?: {
      field?: string;
      text?: string;
    };
  };
  subTotals: {
    enabled: boolean;
    text: string;
    splitTotal?: {
      field?: string;
      text?: string;
    };
  };
  data: {
    dataModel: ChartData.MuzeDataModel;
    totalString: string;
  };
};
export interface FacetSerializer {
  serialize: () => FacetSizeHelpers.FacetSizeItem[];
}

export type UpdateFacetSizesFunction = (layout: FacetSerializer) => void;

export type ColumnVectorInteraction = {
  resize: {
    enabled: boolean;
    sideEffects?: {
      'column-dimension-changer': {
        widths?: UpdateFacetSizesFunction;
      };
    };
  };
};

export type Legend = {
  color?: {
    fields: {
      [key: string]: LegendField;
    };
  };
  backgroundColor?: {
    fields: {
      [key: string]: LegendField;
    };
  };
  size: {
    fields: {
      [key: string]: LegendField;
    };
  };
  interaction?: MuzeInteractionHighlight | MuzeInteractionBrush | MuzeInteractionInverseSelect;
};

export type AxisItem = {
  fieldFormats: ChartTypes.FieldFormat;
  type: Attr.Type;
  axisTitleLocation: ChartTypes.HeaderAxisLocationTypes;
  isY: boolean;
  className?: string;
  format?: ChartTypes.ChartFormat;
};

export type AxisField = {
  show: boolean;
  showAxisName: boolean;
  showInnerTicks: boolean;
  tickFormat: ChartData.MuzeDataFormatFn | undefined;
  domain?: [min: number | null, max: number | null] | Array<number>;
  interpolator?: 'linear' | 'log' | 'pow';
  labels?: {
    rotation?: number | null;
  };
  uniformAxisDomains?: boolean;
  ordering?: Ordering;
  className?: string;
  name?: string;
  bins?: {
    display?: 'startValue' | 'continuous' | 'wholeRange';
    position?: 'center' | 'start';
  };
};

export enum OrderingType {
  NATURAL = 'natural',
  ALPHABETICAL = 'alphabetical',
  FIELD = 'field',
  NESTED = 'nested',
  CUSTOM = 'custom',
}

export type MuzeAggregation = 'count' | 'avg' | 'sum' | 'min' | 'max';
export type OrderingValue = string | number | boolean | null;

export type Ordering = {
  type?: OrderingType;
  direction?: 'asc' | 'desc';
  field?: {
    name: string;
    aggregation: MuzeAggregation;
  };
  values?: readonly OrderingValue[];
  nested?: {
    field: {
      name: string;
      aggregation: MuzeAggregation;
    };
  };
};

export type LegendField = {
  title?: { text: string; show: boolean };
  position?: string;
  range?: readonly string[] | readonly number[];
  repeatRange?: boolean;
  step?: boolean;
  stops?: number | number[];
  show?: boolean;
  domainRangeMap?: { [domainName: string]: string };
  domain?: number[];
  ordering?: Ordering;
  item?: {
    text: {
      formatter?: ChartData.MuzeDataFormatFn;
    };
  };
  splitBy?: {
    field: string | null;
    values?: Record<string, any>;
  };
};

export type MuzeRowsColumnsConfig = {
  facets: Facets;
  headers: Headers;
  totals?: RowColumnTotals;
  layout?: FacetSizeHelpers.FacetSizeItem[];
  interaction?: ColumnVectorInteraction;
  totalMaxWidthRatio?: number;
};

export type MuzeBordersConfig = {
  border: {
    width: number;
    color: string;
    showRowBorders: { top: boolean; bottom: boolean; left: boolean; right: boolean };
    showColBorders: { top: boolean; bottom: boolean; left: boolean; right: boolean };
    showValueBorders: { top: boolean; bottom: boolean; left: boolean; right: boolean };
  };
};

function baseAxisField(props: Partial<AxisField> = {}): AxisField {
  return {
    show: true,
    showAxisName: true,
    showInnerTicks: true,
    tickFormat: (dataInfo: ChartData.MuzeTextDataInfo, contextInfo: ChartData.MuzeContextInfo) => dataInfo.rawValue,
    ...props,
  };
}
